/**
 * Central registry of data-attr values for PostHog heatmaps and autocapture.
 *
 * Naming convention: {page}-{section}-{element}
 * - page: sidebar | dashboard | calendar | marketing | settings | chat | reputation | groups | booking
 * - section: logical group within the page
 * - element: the specific interactive element
 *
 * Every interactive element in the app should reference a constant from this
 * file so that PostHog labels are consistent and grep-able.
 */

// ---------------------------------------------------------------------------
// Sidebar & Global Navigation
// ---------------------------------------------------------------------------
export const SIDEBAR = {
  toggle: 'sidebar-toggle',
  navDashboard: 'sidebar-nav-dashboard',
  navCalendar: 'sidebar-nav-calendar',
  navMarketing: 'sidebar-nav-marketing',
  navReputation: 'sidebar-nav-reputation',
  navBookingEngine: 'sidebar-nav-booking-engine',
  navGroups: 'sidebar-nav-groups',
  navAiReasoning: 'sidebar-nav-ai-reasoning',
  navTasks: 'sidebar-nav-tasks',
  navSettings: 'sidebar-nav-settings',
  tutorialRestart: 'sidebar-tutorial-restart',
  search: 'sidebar-search',
  userProfile: 'sidebar-user-profile',
  logout: 'sidebar-logout',
} as const;

export const NAV = {
  userDropdownTrigger: 'nav-user-dropdown-trigger',
  dropdownLogout: 'nav-dropdown-logout',
} as const;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export const DASHBOARD = {
  // Date selectors
  monthSelect: 'dashboard-month-select',
  yearSelect: 'dashboard-year-select',

  // Notification banner
  notificationDismiss: 'dashboard-notification-dismiss',
  notificationViewReport: 'dashboard-notification-view-report',

  // Revenue chart
  revenueFilterToggle: 'dashboard-revenue-filter-toggle',
  revenueFilterOption: 'dashboard-revenue-filter-option',
  revenueMarketAdjCheckbox: 'dashboard-revenue-market-adj-checkbox',
  revenueRoomsOooCheckbox: 'dashboard-revenue-rooms-ooo-checkbox',
  revenueStlyCheckbox: 'dashboard-revenue-stly-checkbox',
  revenueYearFilterToggle: 'dashboard-revenue-year-filter-toggle',
  revenueYearFilterOption: 'dashboard-revenue-year-filter-option',

  // Trends chart
  trendsFilterToggle: 'dashboard-trends-filter-toggle',
  trendsFilterOption: 'dashboard-trends-filter-option',
  trendsYearFilterToggle: 'dashboard-trends-year-filter-toggle',
  trendsYearFilterOption: 'dashboard-trends-year-filter-option',

  // Summary / Pie chart
  summaryMonthPicker: 'dashboard-summary-month-picker',

  // Detailed revenue table
  detailedRevenuePeriodToggle: 'dashboard-detailed-revenue-period-toggle',

  // Pickup report
  pickupPacingModalTrigger: 'dashboard-pickup-pacing-modal-trigger',
  pickupInfoTooltip: 'dashboard-pickup-info-tooltip',
  pickupPeriodToggle: 'dashboard-pickup-period-toggle',
  pickupLastPeriodFilter: 'dashboard-pickup-last-period-filter',
  pickupNextPeriodFilter: 'dashboard-pickup-next-period-filter',

  // Calendar widget
  calendarMonthPicker: 'dashboard-calendar-month-picker',
  calendarDayCell: 'dashboard-calendar-day-cell',
} as const;

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------
export const CALENDAR = {
  // Toolbar
  viewToggle: 'calendar-view-toggle',
  refreshButton: 'calendar-refresh-button',
  optionsTrigger: 'calendar-options-trigger',
  monthPicker: 'calendar-month-picker',

  // Options popover
  optionRoomTypeAll: 'calendar-option-room-type-all',
  optionRoomType: 'calendar-option-room-type',
  optionPickupToggle: 'calendar-option-pickup-toggle',
  optionPaceToggle: 'calendar-option-pace-toggle',
  optionFloorCeilingToggle: 'calendar-option-floor-ceiling-toggle',
  optionBookingCurveToggle: 'calendar-option-booking-curve-toggle',
  optionEventsToggle: 'calendar-option-events-toggle',
  optionEventFilterAll: 'calendar-option-event-filter-all',
  optionEventFilter: 'calendar-option-event-filter',

  // Grid cells
  dayCell: 'calendar-day-cell',

  // Price adjustment modal
  priceModalClose: 'calendar-price-modal-close',
  priceBulkToggle: 'calendar-price-bulk-toggle',
  priceDateRangeSelector: 'calendar-price-date-range-selector',
  priceSingleDatePicker: 'calendar-price-single-date-picker',
  priceBaseRoomInput: 'calendar-price-base-room-input',
  priceFloorInput: 'calendar-price-floor-input',
  priceCeilingInput: 'calendar-price-ceiling-input',
  priceRoomTypeInput: 'calendar-price-room-type-input',
  priceResetButton: 'calendar-price-reset-button',
  priceSaveButton: 'calendar-price-save-button',

  // Confirm reset modal
  resetConfirmButton: 'calendar-reset-confirm-button',
  resetCancelButton: 'calendar-reset-cancel-button',

  // Booking curve
  bookingCurveExpand: 'calendar-booking-curve-expand',
  bookingCurveModalClose: 'calendar-booking-curve-modal-close',

  // Mobile bottom sheet
  mobileSheetClose: 'calendar-mobile-sheet-close',

  // Summary panel
  summaryEventHide: 'calendar-summary-event-hide',
  summaryShowAllEvents: 'calendar-summary-show-all-events',
} as const;

// ---------------------------------------------------------------------------
// Marketing
// ---------------------------------------------------------------------------
export const MARKETING = {
  // Tabs
  tabItem: 'marketing-tab-item',

  // Period filter
  filterToggle: 'marketing-filter-toggle',
  filterYtd: 'marketing-filter-ytd',
  filterYear: 'marketing-filter-year',
  filterMonth: 'marketing-filter-month',
  filterYearSelect: 'marketing-filter-year-select',
  filterMonthSelect: 'marketing-filter-month-select',
  filterMonthYearSelect: 'marketing-filter-month-year-select',
  filterApply: 'marketing-filter-apply',

  // Notification banner
  notificationDismiss: 'marketing-notification-dismiss',
  notificationViewReport: 'marketing-notification-view-report',

  // Ads preview
  adsHotelNameInput: 'marketing-ads-hotel-name-input',
  adsSuggestionItem: 'marketing-ads-suggestion-item',
  adsWebsiteInput: 'marketing-ads-website-input',
  adsGenerateButton: 'marketing-ads-generate-button',
  adsMobileToggle: 'marketing-ads-mobile-toggle',
  adsDesktopToggle: 'marketing-ads-desktop-toggle',
  adsPrevButton: 'marketing-ads-prev-button',
  adsNextButton: 'marketing-ads-next-button',
  adsCarouselDot: 'marketing-ads-carousel-dot',

  // Pie chart
  chartMonthPicker: 'marketing-chart-month-picker',

  // Guest review chart
  reviewsMonthPicker: 'marketing-reviews-month-picker',

  // Feeder markets
  marketsMonthPicker: 'marketing-markets-month-picker',
} as const;

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
export const SETTINGS = {
  // Tabs
  tabItem: 'settings-tab-item',
  saveButton: 'settings-save-button',
  cancelButton: 'settings-cancel-button',

  // Competitor sub-tabs
  competitorSubTab: 'settings-competitor-sub-tab',
  competitorUspTextarea: 'settings-competitor-usp-textarea',
  competitorAmenitiesTextarea: 'settings-competitor-amenities-textarea',

  // AI Chat sub-tabs
  aiChatSubTab: 'settings-ai-chat-sub-tab',
  aiChatSaveButton: 'settings-ai-chat-save-button',
  aiChatCancelButton: 'settings-ai-chat-cancel-button',

  // Season & Events
  seasonAddButton: 'settings-season-add-button',
  seasonYearSelect: 'settings-season-year-select',
  seasonSelect: 'settings-season-select',
  seasonRefreshButton: 'settings-season-refresh-button',
  seasonDeleteButton: 'settings-season-delete-button',
  seasonSaveButton: 'settings-season-save-button',
  seasonCancelButton: 'settings-season-cancel-button',
  seasonCalendarMonthPicker: 'settings-season-calendar-month-picker',

  // Season form modal
  seasonFormNameInput: 'settings-season-form-name-input',
  seasonFormStartDate: 'settings-season-form-start-date',
  seasonFormEndDate: 'settings-season-form-end-date',
  seasonFormWeekdayFloor: 'settings-season-form-weekday-floor',
  seasonFormWeekendFloor: 'settings-season-form-weekend-floor',
  seasonFormWeekdayCeiling: 'settings-season-form-weekday-ceiling',
  seasonFormWeekendCeiling: 'settings-season-form-weekend-ceiling',
  seasonFormSaveButton: 'settings-season-form-save-button',
  seasonFormCancelButton: 'settings-season-form-cancel-button',

  // Delete confirmation
  seasonDeleteConfirm: 'settings-season-delete-confirm',
  seasonDeleteCancel: 'settings-season-delete-cancel',

  // Copy season buttons and overwrite confirmation
  seasonCopyAllButton: 'settings-season-copy-all-button',
  seasonCopyRowButton: 'settings-season-copy-row-button',
  seasonCopyOverwriteConfirm: 'settings-season-copy-overwrite-confirm',
  seasonCopyOverwriteCancel: 'settings-season-copy-overwrite-cancel',
} as const;

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------
export const CHAT = {
  // Message input & send
  messageInput: 'chat-message-input',
  sendButton: 'chat-send-button',
  voiceInputButton: 'chat-voice-input-button',

  // Sample questions
  sampleQuestion: 'chat-sample-question',

  // Header controls
  expandToggle: 'chat-expand-toggle',
  closeButton: 'chat-close-button',
  conversationSelector: 'chat-conversation-selector',
  newChatButton: 'chat-new-chat-button',
  selectConversation: 'chat-select-conversation',
  deleteConversation: 'chat-delete-conversation',
  deleteConfirm: 'chat-delete-confirm',
  deleteCancel: 'chat-delete-cancel',

  // Message actions
  speakButton: 'chat-speak-button',
  scrollToBottom: 'chat-scroll-to-bottom',

  // Hotel metrics widget
  widgetMetricSelector: 'chat-widget-metric-selector',
  widgetSelectMetric: 'chat-widget-select-metric',
  widgetStlyToggle: 'chat-widget-stly-toggle',
  widgetExpand: 'chat-widget-expand',

  // Legacy chatbot (Chatbot.tsx)
  legacyLoadHistory: 'chat-legacy-load-history',
} as const;

// ---------------------------------------------------------------------------
// Reputation (Reviews)
// ---------------------------------------------------------------------------
export const REPUTATION = {
  tabToggle: 'reputation-tab-toggle',
  periodFilter: 'reputation-period-filter',
  settingsButton: 'reputation-settings-button',
} as const;

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------
export const GROUPS = {
  mobileMenuButton: 'groups-mobile-menu-button',
  newQuoteButton: 'groups-new-quote-button',
} as const;

// ---------------------------------------------------------------------------
// Booking Engine
// ---------------------------------------------------------------------------
export const BOOKING = {
  tabDesign: 'booking-tab-design',
  tabSettings: 'booking-tab-settings',
} as const;

// ---------------------------------------------------------------------------
// Command Palette
// ---------------------------------------------------------------------------
export const COMMAND_PALETTE = {
  trigger: 'command-palette-trigger',
  container: 'command-palette-container',
  input: 'command-palette-input',
  item: 'command-palette-item',
} as const;

// ---------------------------------------------------------------------------
// Sidebar Preview Cards
// ---------------------------------------------------------------------------
export const SIDEBAR_PREVIEW = {
  card: 'sidebar-preview-card',
} as const;
